'use client'

import { algoliaProductObjectId } from '@repo/algolia/config'
import { categoryFallback, listFallback, priceFallback } from '@repo/elevar/mapper'
import { ElevarViewItemListEventData } from '@repo/elevar/types'
import { LocaleIdentifier } from '@repo/markets/types'
import { LOCAL_STORAGE_LIST, setInStorage } from '@repo/storage/service'
import useIdleObserver from '@repo/ui/hooks/useIdleObserver'
import { createProductUrl } from '@repo/url/url'
import { extractLastPart } from '@repo/utils/text'
import { usePathname, useSearchParams } from 'next/navigation'
import { FunctionComponent } from 'react'
import { useTelemetry } from '../../providers/TelemetryProvider'
import { CollectionDto, CollectionItemsDto, SanityCollectionItem } from '../../types/collection.types'
import { PageDto } from '../../types/page.types'

interface Props {
  pageDto: PageDto
  collectionDto: CollectionDto
  collectionItemsDto: CollectionItemsDto
  locale: LocaleIdentifier
}

const CollectionTelemetry: FunctionComponent<Props> = ({ pageDto, collectionDto, collectionItemsDto, locale }) => {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const list = collectionDto?.collection?.urlSlug?.current
  const { trackElevarViewItemListEvent, trackAlgoliaViewObjectIDsEvent } = useTelemetry()

  // Keep track of last viewed list so we can store this in the cart line items,
  // and in turn, inform Elevar of which list cart line items were coming from.
  if (list) {
    setInStorage(LOCAL_STORAGE_LIST, list)
  }

  useIdleObserver(
    () => {
      if (collectionItemsDto.productCount > 0) {
        const items = collectionItemsDto.items.filter((item) => item._type !== 'contentPreview')

        trackElevarViewItemListEvent(
          mapCollectionToElevarViewItemListData(
            pageDto,
            // Elevar payload cannot be too large; a bit arbitrary here.
            items.slice(0, 20),
            locale,
            list,
          ),
        )

        trackAlgoliaViewObjectIDsEvent({
          eventName: 'View Collection Page',
          objectIDs: items.map((item) => algoliaProductObjectId(item.urlSlug)),
        })
      }
    },
    {
      timeout: 1000, // Run after 1 sec. if browser does not support idle callbacks.
      delay: 1000, // Run action after 1 sec. to ensure dl_user_data event runs first.
    },
    // Dispatch events when pathname (e.g. '/collection/sandals') or URL filters params change.
    [pathname, searchParams],
  )

  return null
}

const mapCollectionToElevarViewItemListData = (
  pageDto: PageDto,
  items: SanityCollectionItem[],
  locale: LocaleIdentifier,
  list?: string,
): Omit<ElevarViewItemListEventData, 'user_properties'> => {
  return {
    ecommerce: {
      currencyCode: pageDto.shopifyInstance.presentmentCurrency,
      impressions: items.flatMap((item, i) => {
        if (!item?.variantSkus?.[0] || !item.title || !item.vendor || !item?.variantTitles?.[0]) {
          return []
        }

        return {
          id: item?.variantSkus?.[0],
          name: item.title,
          brand: item.vendor,
          category: item.productType || categoryFallback,
          variant: item?.variantTitles?.[0],
          price: (item.maxVariantPrice || 0).toFixed(2),
          position: i + 1,
          list: list || listFallback,
          product_id: extractLastPart(item.gid || ''),
          variant_id: extractLastPart(item?.variantIds?.[0] || ''),
          compare_at_price: (item.discount || 0) > 0 ? ((item.maxVariantPrice || 0) + (item.discount || 0)).toFixed(2) : priceFallback,
          image: item.thumbnailUrl || '',
          url: createProductUrl(item.urlSlug, locale),
        }
      }),
    },
  }
}

export default CollectionTelemetry
